import { useState, useEffect } from 'react';
import { apiFetch } from '../../../../utils/api';
import { sortFeatureValues } from '../../../../utils/sortingUtils';
import ModalPortal from '../../../../components/ModalPortal';

const getValueCode = (value) => (
  value && typeof value === 'object'
    ? String(value.value ?? '')
    : String(value ?? '')
);

const getValueLabel = (value) => (
  value && typeof value === 'object'
    ? String(value.displayText || value.value || '')
    : String(value ?? '')
);

const featureLabel = (allFeatures, name) => {
  const feature = allFeatures?.find(v => v.name === name);
  return feature?.displayText || name;
};

const selectionValueLabel = (allFeatures, featureName, code) => {
  const feature = allFeatures?.find(v => v.name === featureName);
  const match = Array.isArray(feature?.values)
    ? feature.values.find(val => getValueCode(val) === String(code ?? ''))
    : null;
  return match ? getValueLabel(match) : String(code ?? '');
};

const sortConflictOptions = (values, validValueCodes) => {
  const allowed = [];
  const locked = [];
  for (const val of sortFeatureValues(values)) {
    if (validValueCodes.includes(getValueCode(val))) allowed.push(val);
    else locked.push(val);
  }
  return [...allowed, ...locked];
};

/**
 * ResolveModal - Modal for resolving rule conflicts
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether the modal is visible
 * @param {Object} props.resolveData - Data needed to resolve conflicts
 * @param {Array} props.allFeatures - All feature metadata
 * @param {Function} props.onConfirm - Callback when resolution is confirmed
 * @param {Function} props.onCancel - Callback to cancel resolution
 * @returns {JSX.Element|null} - Rendered modal or null if not shown
 */
export default function ResolveModal({ show, resolveData, allFeatures, onConfirm, onCancel }) {
  const [modalSelections, setModalSelections] = useState({});
  const [modalDomains, setModalDomains] = useState({});
  const [isValidating, setIsValidating] = useState(false);

  useEffect(() => {
    if (show && resolveData) {
      Promise.resolve().then(() => {
        setModalSelections({ ...resolveData.newState });
      });
    }
  }, [show, resolveData]);

  useEffect(() => {
    if (!show || !resolveData) return;
    Promise.resolve().then(() => {
      setIsValidating(true);
    });
    const url = resolveData.familyId ? `/api/products/${resolveData.familyId}/evaluate` : '/api/evaluate';
    apiFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ Selections: modalSelections, FamilyId: resolveData.familyId })
    })
    .then(res => res.json())
    .then(data => {
      setModalDomains(data.availableDomains || {});
      setIsValidating(false);
    })
    .catch(err => {
      console.error(err);
      setIsValidating(false);
    });
  }, [modalSelections, show, resolveData]);

  if (!show || !resolveData) return null;

  const removedSelections = resolveData.removedSelections || [];
  const missingReplacement = removedSelections.some(varName => !modalSelections[varName]);
  const targetFeatureLabel = featureLabel(allFeatures, resolveData.targetFeature);
  const targetValueLabel = selectionValueLabel(allFeatures, resolveData.targetFeature, resolveData.targetValue);

  return (
    <ModalPortal>
    <div className="modal-overlay fade-in">
      <div className="modal-content">
        <h2>Conflict Detected</h2>
        <p>Your selection <strong>{targetFeatureLabel} = {targetValueLabel}</strong> resulted in a conflict.</p>

        <div style={{margin: '1.5rem 0', display: 'flex', flexDirection: 'column', gap: '1rem'}}>
        {removedSelections.map(varName => {
           const featureObj = allFeatures?.find(v => v.name === varName);
           if (!featureObj) return null;
           const validValues = modalDomains[varName] || featureObj.values;
           const validValueCodes = validValues.map(getValueCode);
           const featureName = featureObj.displayText || varName;
           return (
             <div key={varName}>
                <label style={{display:'block', marginBottom:'0.5rem'}}>
                  To resolve this conflict, the feature <strong>{featureName}</strong> should be changed to one of the following values below:
                </label>
                <select 
                   className="input-field" value={modalSelections[varName] || ""}
                   onChange={(e) => {
                     const newSel = { ...modalSelections };
                     if (e.target.value) newSel[varName] = e.target.value;
                     else delete newSel[varName];
                     setModalSelections(newSel);
                   }}
                   style={{width: '100%', padding: '0.5rem', borderRadius: '4px'}}
                >
                   <option value="" disabled>Select a value</option>
                   {Array.isArray(featureObj.values) && sortConflictOptions(featureObj.values, validValueCodes).map(val => {
                       const displayVal = getValueCode(val);
                       const displayLabel = getValueLabel(val);
                       const isValid = validValueCodes.includes(displayVal);
                       return (
                       <option key={displayVal} value={displayVal} disabled={!isValid} className={!isValid ? 'invalid-option' : ''}>
                           {displayLabel} {!isValid ? '(Locked)' : ''}
                       </option>
                   )})}
                </select>
             </div>
           )
        })}
        </div>

        <div className="modal-actions">
          <button onClick={onCancel} className="btn-secondary">Cancel</button>
          <button onClick={() => onConfirm(modalSelections)} disabled={isValidating || missingReplacement} className="btn-primary">Resolve & Confirm</button>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
}
